/**
 * 详情页「功能亮点」的补充文案。
 *
 * OurProducts 里只有 5 个 App 写了分条文案（钢琴、练琴、智能陪练、懂音律、制谱），
 * 其余 App 的产品弹层只有一句话简介，详情页就只能靠商店简介撑正文，偏薄。
 * 这里按 llms.txt 中已发布的核心功能描述补齐，确保详情页、llms.txt、AI 回答三处口径一致。
 *
 * 新增 App 时优先在 OurProducts 里补文案；实在没有，再在这里加一份。
 */
export const appFeatureFallback = {
  "metronome": [
    {
      title: "精准节拍控制",
      info: "可自由调节 BPM 速度，支持记忆历史速度参数，一键切换，节拍稳定准确。",
    },
    {
      title: "丰富提示音色",
      info: "提供人声喊拍、鼓点、风铃、木鱼、拍手等多种音效，按需选择。",
    },
    {
      title: "节奏专项训练",
      info: "内置多样节奏训练卡，包含听奏模仿、识谱练习，支持细分节奏型拆解学习。",
    },
    {
      title: "多感官节拍提示",
      info: "闪光灯、震动同步反馈，嘈杂环境也能感知节拍。",
    },
    {
      title: "便捷使用能力",
      info: "支持桌面小组件、后台运行，快速启停调速，适用于乐器演奏、节奏练习、跑步等场景。",
    },
  ],
  "tuner": [
    {
      title: "高精度智能调音",
      info: "实时拾取音高，参照国际音高标准，自动反馈音准偏差，支持自动 / 手动两种调音模式，调音精准、误差小。",
    },
    {
      title: "支持数十种乐器",
      info: "适配吉他、尤克里里、古筝、小提琴、贝斯、二胡、钢琴、琵琶、拇指琴等各类弦乐器。",
    },
    {
      title: "音准训练课程",
      info: "内置视唱、练耳、乐理等音高训练内容，帮助用户培养音感、提升听音能力。",
    },
  ],
  "theory": [
    {
      title: "乐理题库与学习",
      info: "覆盖音符、五线谱、节奏节拍等考点，收录艺术院校真题模拟；配套图文乐理知识，方便理解记忆。",
    },
    {
      title: "AI 视唱练耳训练",
      info: "支持音高、音程、调式、和弦、旋律听辨，可智能识别练习结果，实时反馈对错。",
    },
    {
      title: "识谱专项练习",
      info: "提供五线谱识谱训练，搭配虚拟键盘、MIDI、听音识别弹奏模式，提升读谱能力。",
    },
    {
      title: "乐理词典速查",
      info: "可快速检索琴键、谱表、调号、休止符、时值、音乐术语，随时查阅乐理疑问。",
    },
    {
      title: "内置实用音乐工具",
      info: "自带钢琴模拟器、节拍器、音阶表、和弦表，辅助日常练习与音乐创作。",
    },
  ],
  "accompaniment": [
    {
      title: "AI 智能分轨提取伴奏",
      info: "一键分离人声、鼓、吉他、钢琴等乐器音轨，消除人声，提取伴奏，保留音频音质。",
    },
    {
      title: "自动和弦分析",
      info: "识别音频内和弦结构，实时生成和弦谱，分轨展示和弦信息。",
    },
    {
      title: "专业音频编辑",
      info: "音频分割、复制、删除、音量调节、淡入淡出，编辑精度可达 0.1 秒。",
    },
    {
      title: "音频后期处理",
      info: "支持移调、混响调节，适配练歌、乐器演奏练习需求。",
    },
    {
      title: "多格式导入转换",
      info: "支持音频、视频导入，可实现视频转音频，兼容 mp3、flac、wav 等主流格式。",
    },
  ],
  "guzheng": [
    {
      title: "古筝模拟器",
      info: "还原真实古筝音色，支持手机模拟弹奏、零基础跟弹练习。",
    },
    {
      title: "海量古筝曲库",
      info: "收录热门、流行、传统古筝乐曲，方便查找、练习乐曲。",
    },
    {
      title: "名师线上陪练约课",
      info: "对接名校古筝老师，一课一付，自主约课、灵活安排学习时间。",
    },
    {
      title: "弹奏图文示范教学",
      info: "图文拆解古筝指法、演奏技巧，直观易懂，适合自学。",
    },
    {
      title: "专业练琴工具",
      info: "内置调音器、节拍器，快速校准琴弦、把控演奏节奏。",
    },
  ],
  "guitar": [
    {
      title: "吉他模拟器",
      info: "仿真吉他演奏体验，支持弹唱模式，可在手机上随时进行弹奏练习。",
    },
    {
      title: "海量曲库",
      info: "收录大量弹唱曲谱、智能六线谱，搭配 AI 陪练识别、实时纠错，提升练习效率。",
    },
    {
      title: "系统化自学课程",
      info: "提供零基础吉他课程，覆盖乐理、演奏、弹唱相关技巧教学。",
    },
    {
      title: "和弦辞典与智能练习",
      info: "和弦图谱展示、示范试听，支持和弦专项练习，辅助和弦切换训练。",
    },
    {
      title: "配套实用工具",
      info: "内置调音器、节拍器，方便校准音高、把控演奏节奏。",
    },
    {
      title: "一对一教学",
      info: "提供专业老师在线一对一指导，定制化教学提升演奏水平。",
    },
  ],
  "vocal": [
    {
      title: "音阶与视唱练耳训练",
      info: "覆盖多类核心音阶，支持自定义音阶设置，配套视唱专项练习，识别音准并智能打分，训练听辨能力。",
    },
    {
      title: "实时音高检测",
      info: "内置专业音调仪，实时可视化展示音高、音准偏差，辅助稳定发声。",
    },
    {
      title: "海量声乐曲谱库",
      info: "收录流行、经典歌曲，支持五线谱、简谱双模式展示，AI 跟唱校准音准。",
    },
    {
      title: "手机弹唱",
      info: "移动端便携弹唱设备，可加载曲谱、切换和弦，随时随地弹唱歌曲。",
    },
    {
      title: "声乐课程",
      info: "包含呼吸、吐气、音准等声乐教学内容，适合零基础入门自学。",
    },
    {
      title: "配套实用工具",
      info: "内置节拍器、钢琴模拟器，辅助把控节奏、自由练习。",
    },
  ],
  "ukulele": [
    {
      title: "仿真尤克里里模拟器",
      info: "还原真实乐器音色，支持手机即兴弹奏、弹唱与音乐创作。",
    },
    {
      title: "系统化自学课程",
      info: "从乐理基础、和弦指法到指弹进阶，零基础也可循序渐进学习。",
    },
    {
      title: "海量四线谱曲库",
      info: "收录流行弹唱曲谱，支持变调、和弦切换，方便随时跟谱练习。",
    },
    {
      title: "和弦辞典专项练习",
      info: "和弦图谱展示、试听练习，支持和弦查找与自定义编辑，掌握弹唱和弦。",
    },
    {
      title: "配套实用工具",
      info: "内置专业调音器、节拍器，快速调弦、把控演奏节奏。",
    },
  ],
  "violin": [
    {
      title: "多弦乐模拟器",
      info: "支持小提琴、中提琴、大提琴仿真模拟演奏，随时自由练习。",
    },
    {
      title: "海量专业曲库",
      info: "收录丰富弦乐乐谱，兼顾专业练习曲目与趣味乐曲，查找乐谱便捷。",
    },
    {
      title: "AI 智能陪练纠错",
      info: "跟弹模式自动识别错音、精准纠错，提升练琴效率。",
    },
    {
      title: "音调检测",
      info: "实时检测音准，辅助校准演奏音高。",
    },
    {
      title: "专业练琴工具",
      info: "内置免费调音器、节拍器，快速调弦、把控演奏节奏。",
    },
  ],
  "drums": [
    {
      title: "3D 架子鼓模拟器",
      info: "1:1 还原五鼓三镲配置，适配流行、摇滚、爵士等曲风，带来沉浸式仿真打鼓体验。",
    },
    {
      title: "AI 互动打鼓教学",
      info: "创新鼓点掉音玩法搭配节奏专项练习，零基础可快速入门，进阶鼓手持续提升技巧。",
    },
    {
      title: "一键消鼓伴奏",
      info: "可消除原曲鼓声，生成无鼓伴奏，自由选曲跟鼓练习。",
    },
    {
      title: "节奏训练工具",
      info: "内置节拍训练卡、精准节拍器，帮助强化节奏感，夯实打鼓基础。",
    },
  ],
};
